import { Fragment, useEffect, useRef, useState } from "react";
import styles from "./style.module.scss";

const BoundingBox = ({
  label,
  bbValues,
  imageDimensions,
  bbContainerDimensions,
  isLoading,
}: BoundingBoxProps) => {
  const [labelContainerStyle, setLabelContainerStyle] = useState<any>();

  const boxRef = useRef<HTMLDivElement>(null);
  const labelContainerRef = useRef<HTMLDivElement>(null);

  const showBox =
    !isLoading &&
    !!bbContainerDimensions.height &&
    !!bbContainerDimensions.width;

  const getTooltipWrapperStyle = () => {
    if (bbContainerDimensions.height > 0 && bbContainerDimensions.width > 0) {
      return {
        height: `${bbContainerDimensions.height}px`,
        width: `${bbContainerDimensions.width}px`,
      };
    } else {
      return undefined;
    }
  };

  const boundingBoxStyle = (boundingBox: number[]) => {
    let heightRatio = 100;
    let widthRatio = 100;

    if (imageDimensions.height > 0 && imageDimensions.width > 0) {
      heightRatio =
        (bbContainerDimensions.height / imageDimensions.height) * 100;
      widthRatio = (bbContainerDimensions.width / imageDimensions.width) * 100;
    }

    if (boundingBox) {
      return {
        left: (boundingBox[0] * widthRatio) / 100,
        top: (boundingBox[1] * heightRatio) / 100,
        width: (boundingBox[2] * widthRatio) / 100,
        height: (boundingBox[3] * heightRatio) / 100,
        display: showBox ? undefined : "none",
      };
    }

    return undefined;
  };

  const getLabelDimensions = (text: string, styles: any) => {
    const tempDiv = document.createElement("div");
    Object.assign(tempDiv.style, styles);
    tempDiv.textContent = text;

    document.body.appendChild(tempDiv);
    const width = tempDiv.offsetWidth;
    const height = tempDiv.offsetHeight;
    document.body.removeChild(tempDiv);

    return { width, height };
  };

  const calculateLabelPosition = (): any => {
    const bBoxDimensions = boundingBoxStyle(bbValues); // Use bounding box dimensions

    if (bBoxDimensions && bbContainerDimensions) {
      const {
        height: bboxHeight,
        width: bboxWidth,
        top: bboxTop,
        left: bboxLeft,
      } = bBoxDimensions;
      const { width: tooltipWidth } = bbContainerDimensions;

      // Mock styles for label to get width before render
      const mockStyles = {
        visibility: "hidden",
        fontSize: "10px",
        fontFamily: "Inter",
        fontWeight: 600,
        padding: "4px",
        width: "max-content",
        height: "max-content",
      };
      if (label) {
        const { width: labelWidth, height: labelHeight } = getLabelDimensions(
          label,
          mockStyles
        );

        // Determine position values
        let position = {};

        const spaceInRightOutsideBBox = tooltipWidth - bboxLeft >= labelWidth;

        const spaceInTopOutsideBBox = bboxTop > labelHeight + 2;

        const biggerWidthThanBBox = labelWidth > bboxWidth;
        const biggerHeightThanBBox = labelHeight > bboxHeight;

        if (biggerWidthThanBBox) {
          position = {
            ...(spaceInRightOutsideBBox ? { left: "-1px" } : { right: "-1px" }),
          };
        } else {
          position = { ...position, left: "-1px" };
        }

        if (spaceInTopOutsideBBox) {
          position = { ...position, top: `-${labelHeight + 2}px` };
        } else {
          if (biggerHeightThanBBox) {
            position = { ...position, bottom: `-${labelHeight + 2}px` };
          } else {
            position = { ...position, top: `0px` };
          }
        }

        if (tooltipWidth - bboxLeft + labelWidth > tooltipWidth) {
          if (
            biggerWidthThanBBox &&
            spaceInTopOutsideBBox &&
            !spaceInRightOutsideBBox
          ) {
            position = {
              ...position,
              maxWidth: bboxWidth + "px",
            };
          }
        }

        setLabelContainerStyle(position);
        return position;
      }
    }
    setLabelContainerStyle({ display: "none" });
    return { display: "none" };
  };

  useEffect(() => {
    calculateLabelPosition();

    //eslint-disable-next-line
  }, [bbContainerDimensions]);

  return (
    <Fragment>
      <div className={styles.tooltipWrapper} style={getTooltipWrapperStyle()}>
        <div className={styles.tooltipConatiner}>
          <div
            className={styles.boundingBox}
            ref={boxRef}
            style={boundingBoxStyle(bbValues)}
          ></div>
        </div>
      </div>

      <div
        className={styles.textTooltipWrapper}
        style={getTooltipWrapperStyle()}
      >
        <div
          className={styles.textTooltipConatiner}
          style={boundingBoxStyle(bbValues)}
        >
          <div
            className={styles.labelContainer}
            ref={labelContainerRef}
            style={labelContainerStyle}
          >
            <span
              className={styles.textLabel}
              title={
                labelContainerStyle && labelContainerStyle.maxWidth ? label : ""
              }
            >
              {label}
            </span>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default BoundingBox;

interface BoundingBoxProps {
  bbValues: number[];
  isLoading: boolean;
  imageDimensions: any;
  label: string | undefined;
  bbContainerDimensions: any;
}
